import Config from "../_config/config";
import myAxios from "./axiositeachWrapper";

const BASE = `${Config.nodeSiteUrl}v1/discussion-board`;

export interface BlogPostBean {
  id: string;
  title: string;
  content: string;
  blogDate: string;
  postedon: string;
  sticky: string;
  depth: string;
  userId: string;
  userName: string;
  userImagePath: string;
  moderatorPermission: boolean;
  commentPermission: boolean;
  caption?: string;
  children: BlogPostBean[];
  comments: CommentBean[];
  question?: string;
  resource?: string;
}

export interface CommentBean {
  id: string;
  statusId: string;
  postId: string;
  content: string;
  createdBy: string;
  createdOn: string;
  userImagePath: string;
  replies: CommentBean[];
}

export interface DiscussionBoardBean {
  id: string;
  name: string;
  icon: string | null;
  acceptStudentPosts: boolean;
  acceptCommentFromRoleIds: string | null;
  blogPostBeans?: BlogPostBean[];
  courseName: string;
  programName: string;
  studyResourceLearningModules: string;
  // True when the board has email notifications enabled (study_resource_master.Config.sendEmails)
  sendEmails?: boolean;
}

export const extractErrorMessage = (e: any): string => {
  const data = e?.response?.data;
  const detail = typeof data?.detail === "string" ? data.detail : "";
  const errField = data?.error;
  let primary = "";
  if (typeof errField === "string") primary = errField;
  else if (errField && typeof errField.message === "string") primary = errField.message;
  else if (typeof data?.message === "string") primary = data.message;
  else if (typeof e?.message === "string") primary = e.message;
  if (!primary) primary = "Something went wrong";
  return detail && detail !== primary ? `${primary}: ${detail}` : primary;
};

export interface CreateBoardPayload {
  name: string;
  description?: string;
  acceptStudentPosts?: boolean;
  acceptCommentFromRoleIds?: string | null;
  icon?: string | null;
  sendToEmails?: string | null;
  status?: number;
}

export interface RoleBean {
  roleId: number;
  roleName: string;
}

class DiscussionBoardService {
  listBoards = () => myAxios.get<DiscussionBoardBean[]>(`${BASE}/boards`);

  createBoard = (body: CreateBoardPayload) =>
    myAxios.post<any>(`${BASE}/boards`, body) as Promise<{ data: { success: boolean; board: DiscussionBoardBean | null } }>;

  getBoard = (id: number | string) => myAxios.get<DiscussionBoardBean>(`${BASE}/boards/${id}`);

  getTopic = (id: number | string) => myAxios.get<BlogPostBean>(`${BASE}/topics/${id}`);

  getQuestionTopic = (questionId: number | string) =>
    myAxios.get<BlogPostBean>(`${BASE}/topics/question/${questionId}`);

  getResourceTopic = (resourceId: number | string) =>
    myAxios.get<BlogPostBean>(`${BASE}/topics/resource/${resourceId}`);

  getDisclaimer = () => myAxios.get<{ disclaimer: { enabled: boolean; name?: string; text?: string } }>(
    `${BASE}/disclaimer`,
  );

  addTopic = (body: { title: string; content: string; discussionBoardId: number | string }) =>
    myAxios.post<any>(`${BASE}/topics`, body) as Promise<{ data: { success: boolean; blogPost: BlogPostBean | null } }>;

  addComment = (body: { topicId: number | string; comment: string }) =>
    myAxios.post<any>(`${BASE}/comments`, body) as Promise<{ data: { success: boolean } }>;

  addReply = (body: { id: number | string; postId: number | string; replyContent: string }) =>
    myAxios.post<any>(`${BASE}/replies`, body) as Promise<{ data: { success: boolean } }>;

  deleteTopicOrComment = (body: { id: number | string; type: "topic" | "comment" | "reply" }) =>
    myAxios.post<any>(`${BASE}/delete`, body) as Promise<{ data: { success: boolean } }>;

  togglePin = (body: { id: number | string }) =>
    myAxios.post<any>(`${BASE}/pin`, body) as Promise<{ data: { sticky: number } }>;

  listRoles = () =>
    myAxios.get<{ success: boolean; data: RoleBean[] }>(
      `${Config.nodeSiteUrl}v1/users/roles`,
    );
}

export default new DiscussionBoardService();
